import { useMemo } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContestation,
  fetchContestation,
  fetchContestationAttachmentDownload,
  fetchContestationAttachments,
  fetchContestations,
  supplementContestation,
  uploadContestationAttachment,
  type Contestation,
  type ContestationAttachment,
  type ContestationObjectType,
  type CreateContestationInput,
} from '@/integrations/contestacao/client';
import { isContestationExpiredError } from '@/integrations/contestacao/errors';

/**
 * A API de contestação escopa a listagem pelo gerador do token e **não** aceita filtro por
 * `object_id`. Para saber o que já foi contestado nesta medição, o portal carrega as contestações
 * do gerador e cruza localmente com os objetos da tela. Enquanto não houver filtro por objeto no
 * serviço, a página é grande de propósito.
 */
const CONTESTATIONS_PAGE_SIZE = 200;

const contestationsKey = ['contestations', 'generator'] as const;

function objectKey(
  objectType: ContestationObjectType,
  objectId: string,
): string {
  return `${objectType}:${objectId}`;
}

/**
 * Contestação encerrada não impede uma nova abertura para o mesmo objeto (RN-03 é por ativa).
 *
 * `EXPIRED` entra aqui: a vencida não bloqueia nada e o gerador pode contestar de novo. Mas ela **não**
 * entra em `RESOLVED_STATUSES` (`InvoiceDetailContent.tsx`) — ninguém a decidiu, então ela não encerra
 * a fase de revisão da medição.
 */
const CLOSED_STATUSES = new Set([
  'ACCEPTED',
  'PARTIALLY_ACCEPTED',
  'REJECTED',
  'CORRECTION_APPLIED',
  'EXPIRED',
]);

export interface ContestationIndex {
  /** Contestação ativa de um objeto, se houver. */
  active: (
    objectType: ContestationObjectType,
    objectId: string,
  ) => Contestation | undefined;
  /** Todas as contestações do objeto, mais recentes primeiro. */
  all: (
    objectType: ContestationObjectType,
    objectId: string,
  ) => Contestation[];
  /**
   * Contestações que bloquearam este ciclo na abertura, mais recentes primeiro.
   *
   * Existe porque cruzar pelos objetos da tela **perde** a contestação cujo objeto saiu da tela:
   * aceitar "coleta não realizada" invalida as transações da coleta, ela desaparece da listagem
   * da medição, e com ela desaparecia o histórico da própria contestação que a impediu.
   *
   * Não substitui `all`, complementa: contestação aberta antes da coluna existir tem a lista
   * vazia e só é encontrada pelo objeto.
   */
  affectingCycle: (cycleId: string) => Contestation[];
}

export function useGeneratorContestations() {
  const query = useQuery({
    queryKey: contestationsKey,
    queryFn: () => fetchContestations({ page_size: CONTESTATIONS_PAGE_SIZE }),
    staleTime: 1000 * 30,
  });

  const index = useMemo<ContestationIndex>(() => {
    const byObject = new Map<string, Contestation[]>();
    const byCycle = new Map<string, Contestation[]>();

    for (const contestation of query.data?.results ?? []) {
      const key = objectKey(contestation.object_type, contestation.object_id);
      const list = byObject.get(key) ?? [];
      list.push(contestation);
      byObject.set(key, list);

      for (const cycleId of contestation.billing_cycle_ids ?? []) {
        const cycleKey = String(cycleId);
        const cycleList = byCycle.get(cycleKey) ?? [];
        cycleList.push(contestation);
        byCycle.set(cycleKey, cycleList);
      }
    }

    const byNewest = (a: Contestation, b: Contestation) =>
      b.created_at.localeCompare(a.created_at);

    for (const list of byObject.values()) list.sort(byNewest);
    for (const list of byCycle.values()) list.sort(byNewest);

    return {
      all: (objectType, objectId) =>
        byObject.get(objectKey(objectType, objectId)) ?? [],
      active: (objectType, objectId) =>
        byObject
          .get(objectKey(objectType, objectId))
          ?.find((c) => !CLOSED_STATUSES.has(c.status)),
      affectingCycle: (cycleId) => byCycle.get(String(cycleId)) ?? [],
    };
  }, [query.data]);

  return { ...query, index };
}

export interface OpenContestationInput extends CreateContestationInput {
  /** Evidência opcional; sobe direto para o S3 depois que a contestação existe. */
  evidence?: File | null;
}

export interface OpenContestationResult {
  contestation: Contestation;
  /**
   * Preenchido quando a contestação foi criada mas a evidência não subiu. A abertura em si
   * **não** é revertida — ela já vale, e a evidência é opcional (RFC S22).
   */
  evidenceError?: string;
}

/**
 * Abre **uma** contestação. Não existe abertura em lote: contestar duas coletas é repetir o fluxo
 * (RFC S22), o que casa com a API, que recebe um `object_id` por chamada.
 *
 * A evidência sobe depois, porque o anexo precisa do id da contestação. Uma falha nesse segundo
 * passo não pode derrubar a mutation: a contestação já existe, o `hold` já foi enfileirado, e
 * rejeitar aqui faria a tela dizer que nada foi registrado — o gerador tentaria de novo e bateria
 * na RN-03 com `409`. Por isso o erro do anexo volta como resultado, não como exceção.
 */
export function useOpenContestation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ evidence, ...input }: OpenContestationInput): Promise<OpenContestationResult> => {
      const contestation = await createContestation(input);
      if (!evidence) return { contestation };

      try {
        await uploadContestationAttachment(contestation.id, evidence);
        return { contestation };
      } catch (error) {
        return {
          contestation,
          evidenceError:
            error instanceof Error
              ? error.message
              : 'Não foi possível enviar o arquivo de evidência.',
        };
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: contestationsKey });
      // A abertura bloqueia o ciclo (RN-16), então o estado exibido na medição muda.
      void queryClient.invalidateQueries({ queryKey: ['invoice-details'] });
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

/** Reenvio da evidência de uma contestação que já existe — usado quando o upload da abertura falha. */
export function useUploadContestationEvidence() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contestationId, file }: { contestationId: string; file: File }) =>
      uploadContestationAttachment(contestationId, file),
    onSuccess: (_result, { contestationId }) => {
      void queryClient.invalidateQueries({ queryKey: contestationsKey });
      void queryClient.invalidateQueries({
        queryKey: ['contestation-attachments', contestationId],
      });
    },
  });
}

/** Resposta do gerador a uma solicitação de informações da Musa. */
export function useSupplementContestation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, message }: { id: string; message: string }) =>
      supplementContestation(id, message),
    onSuccess: (_result, { id }) => {
      void queryClient.invalidateQueries({ queryKey: contestationsKey });
      // A mensagem que acabou de ser enviada é uma entrada da thread, e a thread vem do
      // **detalhe** — sem invalidar aqui, o gerador enviava as informações e não via a própria
      // resposta na conversa até o `staleTime` vencer.
      void queryClient.invalidateQueries({ queryKey: ['contestation', id] });
    },
    onError: (error, { id }) => {
      // Venceu enquanto o gerador respondia: a lista e o detalhe ainda mostram a contestação em
      // curso, e sem recarregar a tela continuaria oferecendo o formulário de complemento.
      if (!isContestationExpiredError(error)) return;
      void queryClient.invalidateQueries({ queryKey: contestationsKey });
      void queryClient.invalidateQueries({ queryKey: ['contestation', id] });
    },
  });
}

/**
 * Thread e anexos das contestações exibidas no histórico.
 *
 * Duas consultas por contestação, e não uma busca só: `messages`/`resolution` só saem do
 * **detalhe** (`GET /contestations/{id}`) — a listagem devolve `null` para os dois, porque
 * calculá-los ali seria uma query por linha no serviço. Anexo é outro endpoint.
 *
 * São poucas contestações por medição (a do ciclo inteiro e as das coletas contestadas), então o
 * custo é proporcional ao que está na tela — não ao histórico do gerador.
 */
export function useContestationThreads(contestationIds: string[]) {
  const details = useQueries({
    queries: contestationIds.map((id) => ({
      queryKey: ['contestation', id] as const,
      queryFn: () => fetchContestation(id),
      staleTime: 1000 * 30,
    })),
  });

  const attachments = useQueries({
    queries: contestationIds.map((id) => ({
      queryKey: ['contestation-attachments', id] as const,
      queryFn: () => fetchContestationAttachments(id),
      staleTime: 1000 * 60,
    })),
  });

  return useMemo(() => {
    const detailById = new Map<string, Contestation>();
    const attachmentsById = new Map<string, ContestationAttachment[]>();

    contestationIds.forEach((id, index) => {
      const detail = details[index]?.data;
      if (detail) detailById.set(id, detail);
      const files = attachments[index]?.data;
      if (files) attachmentsById.set(id, files);
    });

    return {
      /** Detalhe com a conversa; `undefined` enquanto carrega ou se a consulta falhou. */
      detail: (id: string) => detailById.get(id),
      /** Anexos da contestação. Lista vazia é "sem anexo"; `undefined` é "ainda não sei". */
      attachments: (id: string) => attachmentsById.get(id),
      isLoading: details.some((query) => query.isLoading),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contestationIds.join(','), details, attachments]);
}

/**
 * Abre o anexo em outra aba.
 *
 * O link é pedido no clique porque expira: guardá-lo no estado da tela produziria um botão que
 * funciona por alguns minutos e depois falha em silêncio.
 */
export function useOpenContestationAttachment() {
  return useMutation({
    mutationFn: async ({
      contestationId,
      attachmentId,
    }: {
      contestationId: string;
      attachmentId: string;
    }) => {
      const { download_url } = await fetchContestationAttachmentDownload(
        contestationId,
        attachmentId,
      );
      window.open(download_url, '_blank', 'noopener,noreferrer');
    },
  });
}
